import React from "react";
import { Responsive, WidthProvider } from "react-grid-layout/legacy";
import type { Layout, ResponsiveLayouts } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
  defaultPredefinedLayouts,
  EXTERNAL_SLOT_START,
  EXTERNAL_SLOT_END,
} from "../configs/layoutConfig";
import {
  capitalize,
  generateLayout,
  layoutsEqual,
  updateLayoutsWithStatic,
} from "./smartlayout/util";
import type {
  ShowcaseLayoutProps,
  ShowcaseLayoutState,
} from "./smartlayout/type";
const ResponsiveReactGridLayout = WidthProvider(Responsive);

export default class ShowcaseLayout extends React.Component<
  ShowcaseLayoutProps,
  ShowcaseLayoutState
> {
  static defaultProps: Partial<ShowcaseLayoutProps> = {
    className: "layout",
    rowHeight: 30,
    onLayoutChange: function () {},
    cols: { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 },
    initialLayout: generateLayout(defaultPredefinedLayouts),
  };

  private isUpdatingLayout = false;
  private domCache: React.ReactNode[] | null = null;
  private lastLayoutKey: string = "";

  constructor(props: ShowcaseLayoutProps) {
    super(props);
    this.state = {
      currentBreakpoint: "lg",
      compactType: props.compactType ?? "vertical",
      mounted: false,
      layouts: {
        lg: props.initialLayout || generateLayout(defaultPredefinedLayouts),
      },
      allStatic: props.allStatic ?? true,
    };

    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.onCompactTypeChange = this.onCompactTypeChange.bind(this);
    this.onLayoutChange = this.onLayoutChange.bind(this);
    this.onNewLayout = this.onNewLayout.bind(this);
    this.onToggleStatic = this.onToggleStatic.bind(this);
  }

  componentDidMount() {
    this.setState({ mounted: true });
  }

  generateDOM() {
    const layout = this.state.layouts.lg || [];
    const { slotContent = [] } = this.props;
    const allStatic = this.props.allStatic ?? this.state.allStatic;

    // Create a cache key based on layout and props
    const layoutKey = `${layout.length}-${allStatic}-${slotContent.length}`;
    
    // Check if we can reuse cached DOM
    if (this.domCache && this.lastLayoutKey === layoutKey) {
      return this.domCache;
    }

    const result: React.ReactNode[] = [];
    for (let i = 0; i < layout.length; i++) {
      const l = layout[i];
      // Check if this is an external component slot (1-5)
      const isExternalSlot = i >= EXTERNAL_SLOT_START && i <= EXTERNAL_SLOT_END;
      const slotContentIndex = i - EXTERNAL_SLOT_START; // Convert slot index to array index
      const externalContent =
        isExternalSlot && slotContent[slotContentIndex]
          ? slotContent[slotContentIndex]
          : null;
      // Use item's static property or fall back to allStatic
      const isItemStatic = l.static ?? allStatic;

      result.push(
        <div key={l.i || i.toString()} className={isItemStatic ? "static" : ""}>
          {externalContent ? (
            <div className="external-slot-content">{externalContent}</div>
          ) : isItemStatic ? (
            <span
              className="text"
              title="This item is static and cannot be removed or resized."
            >
              Static - {i}
            </span>
          ) : (
            <span className="text">{i}</span>
          )}
        </div>
      );
    }

    // Cache the result
    this.domCache = result;
    this.lastLayoutKey = layoutKey;
    return result;
  }

  onBreakpointChange(breakpoint: string) {
    this.setState({
      currentBreakpoint: breakpoint,
    });
  }

  onCompactTypeChange() {
    if (this.props.onCompactTypeChange) {
      // Controlled: let parent handle the change
      this.props.onCompactTypeChange();
    } else {
      // Uncontrolled: handle internally
      const { compactType: oldCompactType } = this.state;
      const compactType =
        oldCompactType === "horizontal"
          ? "vertical"
          : oldCompactType === "vertical"
            ? null
            : "horizontal";
      this.setState({ compactType });
    }
  }

  onLayoutChange(layout: Layout[], layouts: ResponsiveLayouts) {
    // Prevent recursive calls - if we're already updating, just call parent callback
    if (this.isUpdatingLayout) {
      this.props.onLayoutChange(layout, layouts);
      return;
    }

    // Preserve static state based on allStatic when layouts change
    const allStatic = this.props.allStatic ?? this.state.allStatic;

    // Conditional detection: Only process layout changes when NOT in static mode
    // This prevents unnecessary updates and infinite loops in static mode
    if (allStatic) {
      // In static mode, just call parent callback without processing
      // This prevents the infinite loop while still allowing parent to track changes if needed
      this.props.onLayoutChange(layout, layouts);
      return;
    }

    // Create updated layouts with static property
    const updatedLayouts = updateLayoutsWithStatic(layouts, allStatic);

    // Check if layouts actually changed using efficient comparison
    const layoutsChanged = !layoutsEqual(this.state.layouts, updatedLayouts);

    // Only update state if layouts actually changed (positions/sizes, not just static property)
    if (layoutsChanged) {
      // Invalidate DOM cache when layout changes
      this.domCache = null;
      this.lastLayoutKey = "";
      
      this.isUpdatingLayout = true;
      this.setState({ layouts: updatedLayouts }, () => {
        this.isUpdatingLayout = false;
      });
    }

    // Always call parent's onLayoutChange with the layout array
    this.props.onLayoutChange(layout, updatedLayouts);
  }

  // Adapter for ResponsiveReactGridLayout's onLayoutChange signature
  // Note: ResponsiveReactGridLayout actually passes Layout[] but types expect Layout
  handleResponsiveLayoutChange = (
    layout: Layout[] | Layout,
    layouts: ResponsiveLayouts | Partial<Record<string, Layout>>,
  ) => {
    // ResponsiveReactGridLayout actually passes an array, but types are incorrect
    const layoutArray = Array.isArray(layout) ? layout : [layout];
    const responsiveLayouts = layouts as ResponsiveLayouts;
    this.onLayoutChange(layoutArray, responsiveLayouts);
  };

  onNewLayout() {
    if (this.props.onNewLayout) {
      // Controlled: let parent handle the change
      this.props.onNewLayout();
    } else {
      // Uncontrolled: handle internally
      this.generateNewLayout();
    }
  }

  /**
   * Public method to generate a new layout. Can be called via ref.
   */
  generateNewLayout = () => {
    const newLayout = generateLayout(defaultPredefinedLayouts);
    // Preserve the current static state when generating new layout
    const allStatic = this.props.allStatic ?? this.state.allStatic;
    const updatedLayout = newLayout.map((item) => ({
      ...item,
      static: allStatic,
    }));
    
    // Invalidate DOM cache
    this.domCache = null;
    this.lastLayoutKey = "";
    
    this.setState({
      layouts: { lg: updatedLayout },
    });
  };

  onToggleStatic() {
    if (this.props.onToggleStatic) {
      // Controlled: let parent handle the change
      this.props.onToggleStatic();
    } else {
      // Uncontrolled: handle internally
      const newStaticState = !this.state.allStatic;

      // Update all breakpoints with the new static state
      const updatedLayouts = updateLayoutsWithStatic(
        this.state.layouts,
        newStaticState,
      );

      // Invalidate DOM cache
      this.domCache = null;
      this.lastLayoutKey = "";

      this.setState({
        allStatic: newStaticState,
        layouts: updatedLayouts,
      });
    }
  }

  componentDidUpdate(prevProps: ShowcaseLayoutProps) {
    // Sync controlled props to state
    if (
      prevProps.compactType !== this.props.compactType &&
      this.props.compactType !== undefined
    ) {
      this.setState({ compactType: this.props.compactType });
    }
    if (
      prevProps.allStatic !== this.props.allStatic &&
      this.props.allStatic !== undefined
    ) {
      // Update layouts when allStatic changes
      const updatedLayouts = updateLayoutsWithStatic(
        this.state.layouts,
        this.props.allStatic,
      );
      
      // Invalidate DOM cache
      this.domCache = null;
      this.lastLayoutKey = "";

      this.setState({
        allStatic: this.props.allStatic,
        layouts: updatedLayouts,
      });
    }
    
    // Invalidate cache if slotContent changes
    if (prevProps.slotContent !== this.props.slotContent) {
      this.domCache = null;
      this.lastLayoutKey = "";
    }
  }

  render() {
    const { cols } = this.props;
    const currentCols = cols || ShowcaseLayout.defaultProps.cols || {};
    // Use controlled props if provided, otherwise use state
    const compactType = this.props.compactType ?? this.state.compactType;

    return (
      <div>
        <div>
          Current Breakpoint: {this.state.currentBreakpoint} (
          {currentCols[this.state.currentBreakpoint]} columns)
        </div>
        <div>
          Compaction type: {capitalize(compactType) || "No Compaction"}
        </div>
        <ResponsiveReactGridLayout
          {...this.props}
          layouts={this.state.layouts}
          onBreakpointChange={this.onBreakpointChange}
          onLayoutChange={this.handleResponsiveLayoutChange as any}
          // WidthProvider option
          measureBeforeMount={false}
          // I like to have it animate on mount. If you don't, delete `useCSSTransforms` (it's default `true`)
          // and set `measureBeforeMount={true}`.
          useCSSTransforms={this.state.mounted}
          compactType={compactType}
          preventCollision={!compactType}
        >
          {this.generateDOM()}
        </ResponsiveReactGridLayout>
      </div>
    );
  }
}
